import React, { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { JSONContent } from "@tiptap/core";

export const emptyContent: JSONContent = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

export function CampaignEditor({
  value,
  onChange,
  disabled,
}: {
  value: JSONContent;
  onChange: (value: JSONContent) => void;
  disabled: boolean;
}) {
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [linkError, setLinkError] = useState("");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        link: {
          openOnClick: false,
          protocols: ["https"],
          autolink: false,
          linkOnPaste: false,
        },
      }),
    ],
    shouldRerenderOnTransaction: true,
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class:
          "min-h-[220px] px-5 py-4 text-sm leading-7 text-gray-800 outline-none [&_p]:mb-3 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_a]:text-secondary [&_a]:underline",
        "aria-label": "E-Mail-Inhalt",
        role: "textbox",
        "aria-multiline": "true",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
  });
  useEffect(() => {
    if (editor && JSON.stringify(editor.getJSON()) !== JSON.stringify(value))
      editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);
  if (!editor) return null;
  const buttons = [
    {
      label: "Fett",
      active: editor.isActive("bold"),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      label: "Kursiv",
      active: editor.isActive("italic"),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      label: "Aufzählung",
      active: editor.isActive("bulletList"),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: "Nummerierung",
      active: editor.isActive("orderedList"),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      label: "Link",
      active: editor.isActive("link"),
      run: () => {
        setLinkUrl(editor.getAttributes("link").href || "");
        setLinkError("");
      },
    },
    {
      label: "Zurück",
      active: false,
      run: () => editor.chain().focus().undo().run(),
    },
    {
      label: "Wiederholen",
      active: false,
      run: () => editor.chain().focus().redo().run(),
    },
  ];
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm focus-within:border-secondary/50 focus-within:ring-2 focus-within:ring-secondary/10">
      <div
        className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50/70 p-2"
        role="group"
        aria-label="Text formatieren"
      >
        {buttons.map((b) => (
          <button
            key={b.label}
            type="button"
            disabled={
              disabled ||
              (b.label === "Zurück" && !editor.can().undo()) ||
              (b.label === "Wiederholen" && !editor.can().redo())
            }
            aria-pressed={b.active}
            aria-label={b.label}
            title={b.label}
            onClick={b.run}
            className={`flex h-8 min-w-[32px] items-center justify-center rounded-md px-2 text-sm transition disabled:opacity-40 focus-visible:outline-secondary ${
              b.active
                ? "bg-secondary/10 text-secondary"
                : "text-gray-600 hover:bg-gray-200/70"
            } ${b.label === "Zurück" ? "ml-auto" : ""}`}
          >
            {b.label === "Fett" ? (
              <strong>B</strong>
            ) : b.label === "Kursiv" ? (
              <em className="font-serif text-base">I</em>
            ) : b.label === "Aufzählung" ? (
              "• ≡"
            ) : b.label === "Nummerierung" ? (
              "1. ≡"
            ) : b.label === "Zurück" ? (
              "↶"
            ) : b.label === "Wiederholen" ? (
              "↷"
            ) : (
              b.label
            )}
          </button>
        ))}
      </div>
      {linkUrl !== null && (
        <form
          className="space-y-2 border-b border-gray-200 bg-gray-50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            const href = linkUrl.trim();
            try {
              const url = new URL(href);
              if (url.protocol !== "https:" || url.username || url.password)
                throw new Error();
            } catch {
              setLinkError("Bitte einen gültigen HTTPS-Link eingeben.");
              return;
            }
            editor
              .chain()
              .focus()
              .extendMarkRange("link")
              .setLink({ href })
              .run();
            setLinkUrl(null);
          }}
        >
          <label className="block text-xs font-medium text-gray-700">
            HTTPS-Link
            <input
              autoFocus
              type="url"
              className="form-control mt-1 w-full"
              placeholder="https://…"
              value={linkUrl}
              disabled={disabled}
              onChange={(e) => {
                setLinkUrl(e.target.value);
                setLinkError("");
              }}
            />
          </label>
          {linkError && (
            <p role="alert" className="text-xs text-red-700">
              {linkError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={disabled}
              className="btn btn-primary text-xs"
            >
              Übernehmen
            </button>
            <button
              type="button"
              disabled={disabled}
              className="btn btn-ghost text-xs"
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .unsetLink()
                  .run();
                setLinkUrl(null);
              }}
            >
              Link entfernen
            </button>
            <button
              type="button"
              className="btn btn-ghost text-xs"
              onClick={() => setLinkUrl(null)}
            >
              Schließen
            </button>
          </div>
        </form>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
